import {Children, ClassComponent, Vnode} from 'mithril';
import app from 'flarum/common/app';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import Tooltip from 'flarum/common/components/Tooltip';
import extractText from 'flarum/common/utils/extractText';
import Embed from '../models/Embed';
import formatSize from '../utils/formatSize';

export interface RichImageAttrs {
    embed?: Embed
    src?: string
    alt?: string
    height?: string // Keep string because it's coming from the data-attribute that way and it doesn't matter
    width?: string
}

export default class RichImage implements ClassComponent<RichImageAttrs> {
    copyState: 'copied' | 'loading' | 'error' | 'neutral' = 'neutral';
    resetCopyStateTimeout: number = 0;

    view(vnode: Vnode<RichImageAttrs, this>): Children {
        const {embed, src, alt, height, width} = vnode.attrs;

        return [
            embed ? this.overlay(embed) : null,
            m('img', {
                src,
                alt,
                height: height || embed?.height() || undefined, // if there's no value, undefined makes sure the property won't exist in the DOM
                width: width || embed?.width() || undefined,
            }),
        ];
    }

    overlay(embed: Embed) {
        // If height is not available, the image probably failed to parse. Skip this
        if (!embed.height()) {
            return;
        }

        let copyIcon = 'fas fa-link';

        switch (this.copyState) {
            case 'copied':
                copyIcon = 'fas fa-check';
                break;
            case 'loading':
                copyIcon = 'fas fa-spinner fa-spin';
                break;
            case 'error':
                copyIcon = 'fas fa-exclamation-circle';
                break;
        }

        return m('span.rich-embed-img-over', [
            m('span.rich-embed-img-info', [
                m('span.rich-embed-img-mime', [
                    m('span', (embed.mime() || '').replace('image/', '').toUpperCase()),
                    ' ',
                    m('span', embed.width() + 'x' + embed.height()),
                    ' ',
                    m('span', formatSize(embed.size())),
                ]),
                m('span.rich-embed-img-domain', embed.domain()),
            ]),
            ' ',
            Tooltip.component({
                text: extractText(app.translator.trans('kilowhat-rich-embeds.lib.controls.imgCop' + (this.copyState === 'copied' ? 'ied' : 'y'))),
                tooltipVisible: this.copyState === 'copied' || undefined,
                showOnFocus: false,
            }, Button.component({
                className: 'Button Button--icon',
                icon: copyIcon,
                onclick: (event: Event) => {
                    // Prevent the click on the button from clicking the wrapping link if the image happens to be inside one
                    event.preventDefault();
                    event.stopPropagation();

                    clearTimeout(this.resetCopyStateTimeout);

                    this.copyState = 'loading';

                    navigator.clipboard.writeText(embed.id()!).then(() => {
                        document.querySelector('[aria-describedby]')?.removeAttribute('aria-describedby');

                        this.copyState = 'copied';
                        m.redraw();
                    }).catch(error => {
                        this.copyState = 'error';
                        m.redraw();

                        throw error;
                    }).finally(() => {
                        this.resetCopyStateTimeout = setTimeout(() => {
                            this.copyState = 'neutral';
                            m.redraw();
                        }, 3000) as any as number;
                    });
                },
            })),
            ' ',
            Tooltip.component({
                text: extractText(app.translator.trans('kilowhat-rich-embeds.lib.controls.imgOpen')),
                showOnFocus: false,
            }, LinkButton.component({
                className: 'Button Button--icon',
                icon: 'fas fa-external-link-alt ',
                href: embed.id(),
                external: true,
                target: '_blank',
            })),
        ]);
    }
}
